import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./css/Place.css";

export default function Place({ placeId: propPlaceId, onClose }) {
    const { maybeHandle } = useParams();
    const navigate = useNavigate();

    const isModal = !!propPlaceId;

    const [resolvedPlaceId, setResolvedPlaceId] = useState(
        propPlaceId || null
    );
    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);

    /* --------------------------------------------------
       1️⃣ Resolve slug → placeId (ONLY in page mode)
    -------------------------------------------------- */
    useEffect(() => {
        if (propPlaceId) return; // modal mode
        if (!maybeHandle) {
            setLoading(false);
            return;
        }

        let mounted = true;

        const resolveSlug = async () => {
            try {
                const slugSnap = await getDoc(doc(db, "slugs", maybeHandle));

                if (!mounted) return;

                if (slugSnap.exists()) {
                    setResolvedPlaceId(slugSnap.data().placeId);
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.error("Failed to resolve slug:", err);
                if (mounted) setLoading(false);
            }
        };

        resolveSlug();
        return () => (mounted = false);
    }, [propPlaceId, maybeHandle]);

    /* --------------------------------------------------
       2️⃣ Load place using resolved placeId
    -------------------------------------------------- */
    useEffect(() => {
        if (!resolvedPlaceId) return;

        let mounted = true;

        const loadPlace = async () => {
            try {
                const snap = await getDoc(doc(db, "places", resolvedPlaceId));

                if (!mounted) return;

                if (snap.exists()) {
                    setPlace(snap.data());
                }
            } catch (err) {
                console.error("Failed to load place:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadPlace();
        return () => (mounted = false);
    }, [resolvedPlaceId]);

    /* --------------------------------------------------
       3️⃣ Modal behavior (ONLY in modal mode)
    -------------------------------------------------- */
    useEffect(() => {
        if (!isModal) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", onKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = "";
        };
    }, [isModal, onClose]);

    /* --------------------------------------------------
       4️⃣ Loading / 404
    -------------------------------------------------- */
    if (loading) return null;
    if (!place) return <div className="place-not-found">Place not found</div>;

    /* --------------------------------------------------
       5️⃣ Image Carousel
    -------------------------------------------------- */
    function ImageCarousel({ images }) {
        const [index, setIndex] = useState(0);

        const prev = () =>
            setIndex((i) => (i === 0 ? images.length - 1 : i - 1));

        const next = () =>
            setIndex((i) => (i === images.length - 1 ? 0 : i + 1));

        return (
            <div className="carousel">
                <img
                    src={images[index]}
                    alt={`${place.name} ${index + 1}`}
                    loading="lazy"
                />

                {images.length > 1 && (
                    <>
                        <button
                            className="carousel-btn prev"
                            onClick={prev}
                            aria-label="Previous image"
                        >
                            ‹
                        </button>

                        <button
                            className="carousel-btn next"
                            onClick={next}
                            aria-label="Next image"
                        >
                            ›
                        </button>

                        <div className="carousel-dots">
                            {images.map((_, i) => (
                                <span
                                    key={i}
                                    className={`dot ${i === index ? "active" : ""}`}
                                    onClick={() => setIndex(i)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }

    /* --------------------------------------------------
       6️⃣ Shared Content Layout
    -------------------------------------------------- */
    const content = (
        <>
            {/* LEFT COLUMN */}
            <div className="place-col place-col-image">
                {Array.isArray(place.images) && place.images.length > 0 && (
                    <ImageCarousel images={place.images} />
                )}
            </div>

            {/* RIGHT COLUMN */}
            <div className="place-col place-col-content">
                {isModal && (
                    <button
                        className="modal-close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                )}

                {place.type && (
                    <span className="place-type">
                        {place.type}
                    </span>
                )}

                <h2 className="place-title">{place.name}</h2>

                {(place.city || place.country) && (
                    <p className="place-location">
                        {place.city}
                        {place.city && place.country ? ", " : ""}
                        {place.country}
                    </p>
                )}

                {place.shortDescription && (
                    <p className="place-description">
                        {place.shortDescription}
                    </p>
                )}

                {/* Meta */}
                <div className="place-meta">
                    {place.bestTimeToVisit && (
                        <div>
                            <strong>Best time</strong>
                            <span>{place.bestTimeToVisit}</span>
                        </div>
                    )}
                    {place.recommendedTime && (
                        <div>
                            <strong>Recommended stay</strong>
                            <span>{place.recommendedTime}</span>
                        </div>
                    )}
                    {place.entryFee && (
                        <div>
                            <strong>Entry fee</strong>
                            <span>{place.entryFee}</span>
                        </div>
                    )}
                </div>

                {/* Tips */}
                {Array.isArray(place.tips) && place.tips.length > 0 && (
                    <section className="place-tips">
                        <h4>Travel tips</h4>
                        <ul>
                            {place.tips.map((tip, i) => (
                                <li key={i}>{tip}</li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Only show full-page button in modal mode */}
                {isModal && (
                    <div className="modal-actions">
                        <button
                            className="btn-save"
                            onClick={() => navigate(`/${place.slug}`)}
                        >
                            Open full page →
                        </button>
                    </div>
                )}
            </div>
        </>
    );

    /* --------------------------------------------------
       7️⃣ Page Mode
    -------------------------------------------------- */
    if (!isModal) {
        return (
            <div className="place-page-wrapper two-col">
                {content}
            </div>
        );
    }

    /* --------------------------------------------------
       8️⃣ Modal Mode
    -------------------------------------------------- */
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-box place-modal two-col"
                onClick={(e) => e.stopPropagation()}
            >
                {content}
            </div>
        </div>
    );
}
